/**
 * @file index.tsx
 *
 * @fileoverview Shows organization social links.
 */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import SocialLink from '~/jobiqo/components/Organization/Widgets/InfoBox/components/SocialLink';
import { OrganizationProfile } from '~/jobiqo/types/types';
import { Box } from '~/vcl/atoms/07-box';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { RocketIcon } from '~/vcl/icons/Rocket';
import { LinkedinIcon } from '~/vcl/icons/Linkedin';
import { FacebookIcon } from '~/vcl/icons/Facebook';
import { TwitterIcon } from '~/vcl/icons/Twitter';
import { YoutubeIcon } from '~/vcl/icons/Youtube';
import { InstagramIcon } from '~/vcl/icons/Instagram';
import { XingIcon } from '~/vcl/icons/Xing';
import { TiktokIcon } from '~/vcl/icons/Tiktok';
import { BlueskyIcon } from '~/vcl/icons/Bluesky';
import LinkedinColorIcon from '~/vcl/icons/color/Linkedin';
import FacebookColorIcon from '~/vcl/icons/color/Facebook';
import TwitterColorIcon from '~/vcl/icons/color/Twitter';
import YoutubeColorIcon from '~/vcl/icons/color/Youtube';
import InstagramColorIcon from '~/vcl/icons/color/Instagram';
import XingColorIcon from '~/vcl/icons/color/Xing';
import TiktokColorIcon from '~/vcl/icons/color/Tiktok';
import BlueskyColorIcon from '~/vcl/icons/color/Bluesky';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

export interface OrganizationSocialLinksWidgetProps {
  organization: OrganizationProfile;
}

export const organizationHasSocialLinks = (
  organization: OrganizationProfile,
): boolean =>
  [
    organization.website,
    organization.linkedinProfile,
    organization.facebookProfile,
    organization.twitterProfile,
    organization.youtubeProfile,
    organization.instagramProfile,
    organization.xingProfile,
    organization.tiktokProfile,
    organization.blueSkyProfile,
  ].some(Boolean);

export const OrganizationSocialLinksWidget = ({
  organization,
}: OrganizationSocialLinksWidgetProps) => {
  const intl = useIntl();
  const { APP, ORGANIZATIONS } = useFrontendConfig();
  const SOCIAL_ICONS_COLORED = APP.SOCIAL_ICONS_COLORED;
  const ORGANIZATION_PROFILE_INFO_BOX_FIELDS =
    ORGANIZATIONS.ORGANIZATION_PROFILE_INFO_BOX_FIELDS;

  if (
    !ORGANIZATION_PROFILE_INFO_BOX_FIELDS.socialLinks ||
    !organizationHasSocialLinks(organization)
  ) {
    return null;
  }

  return (
    <Box
      label={
        <FormattedMessage
          id="organization_social_links_label"
          defaultMessage="Find us:"
        />
      }
    >
      <Spacing />
      {organization.website && (
        <SocialLink
          Label={intl.formatMessage({
            id: 'organization_website_label',
            defaultMessage: 'Website',
          })}
          Icon={RocketIcon}
          href={organization.website}
        />
      )}
      {organization.linkedinProfile && (
        <SocialLink
          Label={intl.formatMessage({
            id: 'organization_linkedin_label',
            defaultMessage: 'LinkedIn',
          })}
          Icon={SOCIAL_ICONS_COLORED ? LinkedinColorIcon : LinkedinIcon}
          href={organization.linkedinProfile}
        />
      )}
      {organization.facebookProfile && (
        <SocialLink
          Label={intl.formatMessage({
            id: 'organization_facebook_label',
            defaultMessage: 'Facebook',
          })}
          Icon={SOCIAL_ICONS_COLORED ? FacebookColorIcon : FacebookIcon}
          href={organization.facebookProfile}
        />
      )}
      {organization.twitterProfile && (
        <SocialLink
          Label={intl.formatMessage({
            id: 'organization_twitter_label',
            defaultMessage: 'Twitter',
          })}
          Icon={SOCIAL_ICONS_COLORED ? TwitterColorIcon : TwitterIcon}
          href={organization.twitterProfile}
        />
      )}
      {organization.youtubeProfile && (
        <SocialLink
          Label={intl.formatMessage({
            id: 'organization_youtube_label',
            defaultMessage: 'Youtube',
          })}
          Icon={SOCIAL_ICONS_COLORED ? YoutubeColorIcon : YoutubeIcon}
          href={organization.youtubeProfile}
        />
      )}
      {organization.instagramProfile && (
        <SocialLink
          Label={intl.formatMessage({
            id: 'organization_instagram_label',
            defaultMessage: 'Instagram',
          })}
          Icon={SOCIAL_ICONS_COLORED ? InstagramColorIcon : InstagramIcon}
          href={organization.instagramProfile}
        />
      )}
      {organization.xingProfile && (
        <SocialLink
          Label={intl.formatMessage({
            id: 'organization_xing_label',
            defaultMessage: 'Xing',
          })}
          Icon={SOCIAL_ICONS_COLORED ? XingColorIcon : XingIcon}
          href={organization.xingProfile}
        />
      )}
      {organization.tiktokProfile && (
        <SocialLink
          Label={intl.formatMessage({
            id: 'organization_tiktok_label',
            defaultMessage: 'TikTok',
          })}
          Icon={SOCIAL_ICONS_COLORED ? TiktokColorIcon : TiktokIcon}
          href={organization.tiktokProfile}
        />
      )}
      {organization.blueSkyProfile && (
        <SocialLink
          Label={intl.formatMessage({
            id: 'organization_bluesky_label',
            defaultMessage: 'BlueSky',
          })}
          Icon={SOCIAL_ICONS_COLORED ? BlueskyColorIcon : BlueskyIcon}
          href={organization.blueSkyProfile}
        />
      )}
    </Box>
  );
};

export default OrganizationSocialLinksWidget;
